import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PlainPrice } from './plain-price.tsx';
import { headlineReconciliation, PriceBreakdown } from './price-breakdown.tsx';
import { readerFor } from '../../i18n/localized.ts';
import {
  calculateLaborCents,
  formatCents,
  groupSubtotals,
  minutesToHours,
  onSiteMinutes,
} from '@shared/calculations/pricing.ts';
import { defaultGroupOf, groupsChromeVisible } from '@shared/quotes/groups.ts';
import { calculationInputFor } from '../../hooks/use-quote-editor.ts';
import { formatMinutes } from '../../i18n/format.ts';
import type { QuoteCalculationResult } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import type { Quote } from '@shared/types/index.ts';

function finiteMinutes(minutes: number | undefined): number {
  return typeof minutes === 'number' && Number.isFinite(minutes) ? minutes : 0;
}

/**
 * Running totals, computed locally so the figures move as the estimator works
 * (spec §9.1 — the browser previews, the Worker decides).
 */
export function TotalsPanel({
  quote,
  preview,
  currencyCode,
  profitLine,
}: {
  quote: Quote;
  preview: QuoteCalculationResult;
  currencyCode: string;
  /**
   * The estimated-profit line under the price (0035), when the page has one
   * to show — passed in so this panel stays a pure view of the preview.
   */
  profitLine?: ReactNode;
}) {
  const { t } = useTranslation();
  /* The quote's own language; see `roomLabel` in rooms-step.tsx. */
  const localized = readerFor(quote.language);
  const money = (cents: number) => formatCents(cents, currencyCode, getDateLocale());
  const override = quote.totals.totalOverrideCents;
  const finalTotal = override ?? preview.totalCents;

  /**
   * Under 'monthly' every money figure the engine returns is per month, and
   * `estimatedMinutes` is the month's time — so each money line says so, and
   * the time line names the month. The preview's own echo is what is read,
   * so the labels can never disagree with the numbers under them.
   */
  const monthly = preview.billingBasis === 'monthly';
  const amount = (cents: number) =>
    monthly ? t('totalsPanel.perMonthAmount', { amount: money(cents) }) : money(cents);

  const byArea = quote.areaPricing?.mode === 'area';
  const roundedUp = !byArea && preview.billableMinutes > preview.estimatedMinutes;
  const hasRoomCharges = preview.rooms.some((room) => room.chargeCents !== null);
  const reconciliation = headlineReconciliation(quote, preview);

  /*
    Per-room labor, so the panel answers "what is the kitchen costing me?"
    without opening every room card.

    `preview.rooms` is index-aligned with `quote.rooms` — calculateQuote maps
    the rooms it is given in order — so the two are read together here.

    These are labor only, priced from each room's own billable minutes. They
    deliberately do not add up to the labor line: the billing increment rounds
    the quote's total time up once, not once per room, and fees, discounts and
    tax belong to the quote rather than to any single room. The footnote says
    so rather than leaving the estimator to spot the difference.
  */
  const roomLines = quote.rooms.map((room, index) => {
    const calculated = preview.rooms[index];
    const minutes = calculated?.billableMinutes ?? 0;
    const chargeCents = calculated?.chargeCents ?? null;
    return {
      id: room.id,
      name: localized.text(room.roomName, room.roomNameTranslations),
      quantity: Math.max(1, Math.trunc(room.quantity) || 1),
      minutes,
      // A flat-priced room shows its price. Showing labor it is not being
      // charged would be a figure that appears nowhere in the total.
      isFlatPriced: chargeCents !== null,
      cents: chargeCents ?? calculateLaborCents(minutes, quote.totals.hourlyRateCents),
      selectedTasks: room.services.filter((service) => service.isSelected).length,
      totalTasks: room.services.length,
      isManuallyAdjusted: calculated?.isManuallyAdjusted ?? false,
      sizeCode: room.sizeCode,
      conditionCode: room.conditionCode,
    };
  });

  return (
    <aside
      // The room breakdown can outgrow a short viewport, and a sticky panel
      // taller than the screen hides its own total. Scroll inside instead.
      className="card sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto"
      aria-labelledby="totals-heading"
    >
      <h2 id="totals-heading" className="mb-2 text-lg font-semibold">
        {t('quoteBuilder.priceHeading')}
      </h2>
      {preview.pricingBlocked ? (
        <p className="text-lg font-semibold">{t('areaPricing.unpriced')}</p>
      ) : (
        <PlainPrice quote={quote} previewTotalCents={finalTotal} currencyCode={currencyCode} />
      )}
      <p className="mt-1 text-sm font-semibold">
        {t('quoteWork.aboutTime', {
          time: formatMinutes(
            finiteMinutes(monthly ? preview.estimatedMinutes : preview.routineVisitMinutes || preview.estimatedMinutes),
          ),
        })}
      </p>
      {profitLine ?? null}

      {groupsChromeVisible(quote.groups) && quote.groups ? (
        <section className="mt-4 border-t border-[color:var(--color-line)] pt-3" aria-labelledby="by-group-heading">
          <h3 id="by-group-heading" className="mb-2 text-sm font-semibold">
            {t('totalsPanel.byGroupHeading')}
          </h3>
          <ul className="space-y-3">
            {(() => {
              const fallback = defaultGroupOf(quote.groups ?? [])?.id;
              const orderedGroups = [...quote.groups].sort((left, right) => left.displayOrder - right.displayOrder);
              const partitions = orderedGroups.map((group) =>
                quote.rooms.flatMap((room, index) => {
                  const groupId =
                    room.groupId && quote.groups?.some((candidate) => candidate.id === room.groupId)
                      ? room.groupId
                      : fallback;
                  return groupId === group.id ? [index] : [];
                }),
              );
              const contributions = groupSubtotals(calculationInputFor(quote), partitions);
              return orderedGroups.map((group, groupIndex) => {
                const indexes = partitions[groupIndex] ?? [];
                const contribution = contributions[groupIndex] ?? { minutes: 0, cents: 0 };
                const name = group.name.trim() || t('quoteWork.allRooms');
                const roomCount = indexes.reduce(
                  (sum, index) => sum + Math.max(1, Math.trunc(quote.rooms[index]?.quantity ?? 1) || 1),
                  0,
                );
                const each =
                  group.quantity > 1
                    ? Math.round(contribution.minutes / Math.max(1, group.quantity))
                    : contribution.minutes;
                return (
                  <li key={group.id} className="flex justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {name}
                        {group.quantity > 1 ? (
                          <span className="text-[color:var(--color-muted)]"> ×{group.quantity}</span>
                        ) : null}
                      </p>
                      <p className="text-xs text-[color:var(--color-muted)]">
                        {t(roomCount === 1 ? 'quoteWork.groupEachOne' : 'quoteWork.groupEach', {
                          rooms: roomCount,
                          time: formatMinutes(each),
                        })}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="tabular text-sm font-semibold">{amount(contribution.cents)}</p>
                    </div>
                  </li>
                );
              });
            })()}
          </ul>
          {reconciliation ? <PriceBreakdown lines={reconciliation} amount={amount} /> : null}
        </section>
      ) : null}

      <details className="mt-4 border-t border-[color:var(--color-line)] pt-3">
        <summary className="cursor-pointer text-sm font-semibold">{t('quoteBuilder.advanced')}</summary>
        <p className="mb-3 mt-1 text-xs text-[color:var(--color-muted)]">{t('quoteBuilder.advancedHint')}</p>

        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-[color:var(--color-muted)]">
              {monthly ? t('totalsPanel.estimatedTimeMonthly') : t('totalsPanel.estimatedTime')}
            </dt>
            <dd className="tabular">{formatMinutes(preview.estimatedMinutes)}</dd>
          </div>

          {/* The schedule's number: how long the ordinary visit takes, which
            under monthly is not the figure above. */}
          {monthly ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[color:var(--color-muted)]">{t('totalsPanel.typicalVisit')}</dt>
              <dd className="tabular">{formatMinutes(preview.routineVisitMinutes)}</dd>
            </div>
          ) : null}

          {/*
          The figures above are labor minutes — every person's work summed,
          which is what the price is built from. With more than one cleaner the
          crew is at the property for less time than that, and this is the
          number the customer will experience and the schedule will book. A
          crew of one shows nothing: the two figures are the same, and a row
          repeating the one above would read as a distinction without one.
        */}
          {quote.crewSize > 1 ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[color:var(--color-muted)]">
                {t('totalsPanel.onSite')}
                <span className="block text-xs">{t('totalsPanel.onSiteDetail', { crew: quote.crewSize })}</span>
              </dt>
              <dd className="tabular">{formatMinutes(onSiteMinutes(preview.routineVisitMinutes, quote.crewSize))}</dd>
            </div>
          ) : null}

          {roundedUp ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[color:var(--color-muted)]">
                {t('totalsPanel.billedTime')}
                <span className="block text-xs">
                  {t('totalsPanel.roundedUpTo', { minutes: quote.totals.billingIncrementMinutes })}
                </span>
              </dt>
              <dd className="tabular">{formatMinutes(preview.billableMinutes)}</dd>
            </div>
          ) : null}

          {/*
          A quote priced entirely by the room has no labor to show, and
          "Labor $0.00 — 0 hrs at $65.00/hr" beside a room-by-room breakdown
          reads as something gone wrong. A quote with no priced rooms keeps the
          line whatever it comes to.

          Keyed on whether any room *has* a price, not on the prices summing to
          something: a quote whose rooms are all priced at zero is fully priced
          and would otherwise show the labor line it does not have and hide
          the room line it does.
        */}
          {!hasRoomCharges || preview.laborSubtotalCents !== 0 ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[color:var(--color-muted)]">
                {byArea ? t('areaPricing.area') : t('totalsPanel.labor')}
                <span className="block text-xs">
                  {byArea
                    ? t('areaPricing.squareFeet') + ': ' + quote.areaPricing?.squareFeet
                    : t('totalsPanel.laborDetail', {
                        hours: minutesToHours(preview.billableMinutes),
                        rate: money(quote.totals.hourlyRateCents),
                      })}
                </span>
              </dt>
              <dd className="tabular">{amount(preview.laborSubtotalCents)}</dd>
            </div>
          ) : null}

          {hasRoomCharges ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[color:var(--color-muted)]">{t('totalsPanel.roomCharges')}</dt>
              <dd className="tabular">{amount(preview.roomChargesCents)}</dd>
            </div>
          ) : null}

          {preview.fixedFeesCents !== 0 ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[color:var(--color-muted)]">{t('totalsPanel.fees')}</dt>
              <dd className="tabular">{amount(preview.fixedFeesCents)}</dd>
            </div>
          ) : null}

          {preview.discountCents > 0 ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[color:var(--color-muted)]">{t('totalsPanel.discount')}</dt>
              <dd className="tabular">−{amount(preview.discountCents)}</dd>
            </div>
          ) : null}

          {preview.taxCents > 0 ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[color:var(--color-muted)]">{t('totalsPanel.tax')}</dt>
              <dd className="tabular">{amount(preview.taxCents)}</dd>
            </div>
          ) : null}

          <div className="flex justify-between gap-4 border-t border-[color:var(--color-line)] pt-3 text-lg font-bold">
            <dt>{monthly ? t('totalsPanel.totalMonthly') : t('totalsPanel.total')}</dt>
            <dd className="tabular">{money(finalTotal)}</dd>
          </div>
        </dl>

        {/*
        Directly under the total, and above the room list: the panel scrolls
        internally now, and a quote with enough rooms would otherwise push the
        caveat out of view. The headline figure would then read as the
        calculated one until the estimator scrolled past every room.
      */}
        {override !== null ? (
          <p className="mt-3 rounded-md bg-[color:var(--color-warning-soft)] p-3 text-xs text-[color:var(--color-warning)]">
            {t('totalsPanel.manuallySet', { amount: money(preview.totalCents) })}
            {quote.totals.totalOverrideReason
              ? t('totalsPanel.manuallySetReason', { reason: quote.totals.totalOverrideReason })
              : ''}
          </p>
        ) : null}

        {!byArea && roomLines.length > 0 ? (
          <section className="mt-4 border-t border-[color:var(--color-line)] pt-3" aria-labelledby="by-room-heading">
            <h3 id="by-room-heading" className="mb-2 text-sm font-semibold">
              {t('totalsPanel.byRoomHeading')}
            </h3>
            <ul className="space-y-2">
              {roomLines.map((line) => (
                <li key={line.id} className="flex justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {line.name}
                      {line.quantity > 1 ? (
                        <span className="text-[color:var(--color-muted)]"> ×{line.quantity}</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-[color:var(--color-muted)]">
                      {t('totalsPanel.roomSummary', {
                        size: t(`room.size.${line.sizeCode}`),
                        condition: t(`room.condition.${line.conditionCode}`),
                        selected: line.selectedTasks,
                        total: line.totalTasks,
                      })}
                      {line.isManuallyAdjusted ? ` · ${t('totalsPanel.roomAdjusted')}` : ''}
                      {line.isFlatPriced ? ` · ${t('totalsPanel.roomFlatPriced')}` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="tabular text-sm">{money(line.cents)}</p>
                    <p className="tabular text-xs text-[color:var(--color-muted)]">{formatMinutes(line.minutes)}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-[color:var(--color-muted)]">
              {t('totalsPanel.byRoomFootnote', { rate: money(quote.totals.hourlyRateCents) })}
              {roomLines.some((line) => line.isFlatPriced) ? ` ${t('totalsPanel.byRoomFlatFootnote')}` : ''}
            </p>
          </section>
        ) : null}

        <p className="mt-3 text-xs text-[color:var(--color-muted)]">{t('totalsPanel.footnote')}</p>
      </details>
    </aside>
  );
}
